import { useMemo } from "react";

import type { Provider, Ratio, ProviderCapability, Tier } from "../api";
import { useProviderCapabilities } from "./useProviderCapabilities";

export type ProviderConstraintsMode = "t2i" | "i2i";

export interface ProviderConstraints {
  cap: ProviderCapability | undefined;
  ratios: Ratio[];
  tiers: Tier[];
  maxCount: number;
  defaultRatio: Ratio | undefined;
  defaultResolutionTier: Tier | undefined;
  /** 检查 (ratio, tier) 组合是否合法 — combinationDeny 黑名单 + supported 列表交集。 */
  isCombinationLegal: (ratio: Ratio, tier: Tier) => boolean;
  /** 真实 (ratio, tier) → [W, H] 像素映射,UI chip tooltip 用;cap.tierRatioSize 缺省时返 null。 */
  resolveSize: (ratio: Ratio, tier: Tier) => [number, number] | null;
  isLoading: boolean;
}

export interface UseProviderConstraintsOptions {
  mode?: ProviderConstraintsMode;
  thinking?: boolean;
}

const TIER_ORDER: Record<Tier, number> = {
  "1K": 1,
  "2K": 2,
  "3K": 3,
  "4K": 4,
};

const EMPTY: ProviderConstraints = {
  cap: undefined,
  ratios: [],
  tiers: [],
  maxCount: 0,
  defaultRatio: undefined,
  defaultResolutionTier: undefined,
  isCombinationLegal: () => false,
  resolveSize: () => null,
  isLoading: false,
};

/**
 * provider 约束 single source of truth hook。海报三页(FreeCreate / ApplyTemplate /
 * PosterTemplateEdit)共用,按 (provider, mode, thinking) 派生当前可选 ratios / tiers / maxCount。
 *
 * 语义:
 *   - thinking=true:走 cap.thinking 子能力(OPENAI Responses API 限定,固定 size,不支持 4:5,maxCount=1)
 *   - mode='i2i':tiers 收窄到 cap.i2i.maxResolutionTier,maxCount 走 cap.i2i.maxCount
 *   - 默认 t2i:用 cap.supportedRatios + cap.supportedResolutionTiers + cap.maxCount
 *   - isCombinationLegal:除 supported 列表外,还要不在 combinationDeny 黑名单
 *   - capabilities 没拉回时返空数组(isLoading=true),UI 应展示 loading 不走兜底硬编码
 */
export function useProviderConstraints(
  provider: Provider | undefined,
  options: UseProviderConstraintsOptions = {}
): ProviderConstraints {
  const { mode = "t2i", thinking = false } = options;
  const capsQ = useProviderCapabilities();

  return useMemo(() => {
    const isLoading = capsQ.isLoading;
    const cap = provider ? capsQ.data?.providers?.[provider] : undefined;

    if (!cap) {
      return { ...EMPTY, isLoading };
    }

    const resolveSize = (r: Ratio, t: Tier): [number, number] | null => {
      const map = cap.tierRatioSize;
      if (!map) return null;
      const wh = map[`${t}|${r}`];
      return wh ? [wh[0], wh[1]] : null;
    };

    if (thinking) {
      const th = cap.thinking;
      if (!th?.enabled) {
        return {
          ...EMPTY,
          cap,
          defaultRatio: cap.defaultRatio,
          defaultResolutionTier: cap.defaultResolutionTier,
          isLoading,
        };
      }
      return {
        cap,
        ratios: th.supportedRatios,
        tiers: th.supportedResolutionTiers,
        maxCount: th.maxCount,
        defaultRatio: th.supportedRatios[0],
        defaultResolutionTier: th.supportedResolutionTiers[0],
        isCombinationLegal: (r, t) =>
          th.supportedRatios.includes(r) && th.supportedResolutionTiers.includes(t),
        resolveSize,
        isLoading,
      };
    }

    let tiers = cap.supportedResolutionTiers;
    let maxCount = cap.maxCount;
    if (mode === "i2i" && cap.i2i) {
      const maxOrd = TIER_ORDER[cap.i2i.maxResolutionTier];
      tiers = tiers.filter((t) => TIER_ORDER[t] <= maxOrd);
      maxCount = cap.i2i.maxCount;
    }

    const deny = cap.combinationDeny ?? [];

    return {
      cap,
      ratios: cap.supportedRatios,
      tiers,
      maxCount,
      defaultRatio: cap.defaultRatio,
      defaultResolutionTier: cap.defaultResolutionTier,
      isCombinationLegal: (r, t) => {
        if (!cap.supportedRatios.includes(r)) return false;
        if (!tiers.includes(t)) return false;
        return !deny.some((d) => d.ratio === r && d.tier === t);
      },
      resolveSize,
      isLoading,
    };
  }, [provider, mode, thinking, capsQ.data, capsQ.isLoading]);
}
